import { GetStaticProps, NextPage } from 'next';
import { Container, Row, Col } from 'reactstrap';
import Image from 'next/image';
import { Client } from '../utils';
import { Layout, SeoMeta } from '../components/Layout';
import { LinkTree } from '../components/Linktree';
import { ImageComponent } from '../components/Media';
import { Section } from '../components/Sections';
import { RichText } from 'prismic-reactjs';

interface Props {
  content?: any;
  image?: Array<Image>;
}

interface Image {
  url?: string;
  alt?: string;
  dimensions?: {
    width: number;
    height: number;
  };
}

const AboutTheArt: NextPage<Props> = ({ content }) => {
  return (
    <Layout>
      <SeoMeta
        title={RichText.asText(content.seo__title) || 'Astoria West'}
        desc={RichText.asText(content.seo__description)}
        socialImage={content.seo__image}
      />
      {content.collage_hero && content.collage_hero.url && (
        <Section>
          <Container fluid>
            <ImageComponent
              src={content.collage_hero.url}
              alt={content.collage_hero.alt}
              width={content.collage_hero.dimensions.width}
              height={content.collage_hero.dimensions.height}
              position="left"
              offset="left-full"
              mobileOffset
            />
            {content.subheadline &&
              RichText.asText(content.subheadline) !== '' && (
                <h2 className="font--lg font--secondary font--bold">
                  {RichText.asText(content.subheadline)}
                </h2>
              )}
          </Container>
        </Section>
      )}

      {((content.headline && RichText.asText(content.headline) !== '') ||
        content.cover_images.length > 0 ||
        (content.introduction_text_left &&
          RichText.asText(content.introduction_text_left) !== '') ||
        (content.introduction_text_right &&
          RichText.asText(content.introduction_text_right) !== '') ||
        (content.image_dj && content.image_dj.url)) && (
        <Section hasBottomMobile={false}>
          <Container fluid>
            {content.headline && RichText.asText(content.headline) !== '' && (
              <Row>
                <Col
                  md={{
                    size: 8,
                    offset: 4,
                  }}
                >
                  <h1 className="font--xxl font--primary">
                    {RichText.asText(content.headline)}
                  </h1>
                </Col>
              </Row>
            )}
            <Row>
              <Col md={3} lg={3}>
                {content.cover_images.length > 0 &&
                  // @ts-ignore
                  content.cover_images.map((image, index) => {
                    return (
                      <div
                        key={index}
                        className={index != 0 ? 'm-t-image' : ''}
                      >
                        <ImageComponent
                          src={image.cover_image.url}
                          alt={image.cover_image.alt}
                          alignment="square"
                          offset="left"
                        />
                      </div>
                    );
                  })}
              </Col>
              <Col
                md={{
                  size: 8,
                  offset: 1,
                }}
                lg={{
                  size: 8,
                  offset: 1,
                }}
                className="m-t--sm"
              >
                {((content.introduction_text_left &&
                  RichText.asText(content.introduction_text_left) !== '') ||
                  (content.introduction_text_right &&
                    RichText.asText(content.introduction_text_right) !==
                      '')) && (
                  <Row>
                    <Col md={12} lg={6}>
                      <p>{RichText.asText(content.introduction_text_left)}</p>
                    </Col>
                    <Col md={12} lg={6} className="m-t--sm">
                      <p>{RichText.asText(content.introduction_text_right)}</p>
                    </Col>
                  </Row>
                )}
                {content.image_dj && content.image_dj.url && (
                  <Row>
                    <Col
                      md={{
                        size: 12,
                        offset: 0,
                      }}
                      lg={{
                        size: 8,
                        offset: 4,
                      }}
                      className="m-t"
                    >
                      <ImageComponent
                        src={content.image_dj.url}
                        alt={content.image_dj.alt}
                        alignment="vertical"
                        offset="right-full"
                      />
                    </Col>
                  </Row>
                )}
              </Col>
            </Row>
          </Container>
        </Section>
      )}
      {((content.image_ted_at_home && content.image_ted_at_home.url) ||
        (content.text_ted_at_home &&
          RichText.asText(content.text_ted_at_home) !== '') ||
        content.links.length > 0) && (
        <Section>
          <Container fluid>
            <Row>
              <Col md={7}>
                {content.image_ted_at_home.url && (
                  <div className="m-t-image--sm">
                    <ImageComponent
                      src={content.image_ted_at_home.url}
                      alt={content.image_ted_at_home.alt}
                      alignment="horizontal"
                      offset="left"
                    />
                  </div>
                )}
                {RichText.asText(content.text_ted_at_home) !== '' && (
                  <div className="m-t-image">
                    <p className="font--md">
                      {RichText.asText(content.text_ted_at_home)}
                    </p>
                  </div>
                )}
              </Col>
              <Col
                md={{
                  size: 3,
                  offset: 2,
                }}
                lg={{
                  size: 2,
                  offset: 3,
                }}
              >
                {content.links.length > 0 && (
                  <div className="m-t">
                    <LinkTree links={content.links} />
                  </div>
                )}
              </Col>
            </Row>
          </Container>
        </Section>
      )}
    </Layout>
  );
};

export const getStaticProps: GetStaticProps = async () => {
  // https://prismic.io/docs/technologies/query-by-type-rest-api
  const res = await Client.query('[at(document.type, "about-the-art")]');
  const data = res.results.map((content: { data: any }) => {
    return content.data;
  });
  const content = data[0];

  return {
    props: {
      content,
    },
  };
};

export default AboutTheArt;
